import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import FeedbackCategoryPicker from './feedbackCategoryPicker';
import FeedbackConfirmation from './feedbackConfirmation';
import FeedbackForm from './feedbackForm';
import Icon from '../common/containers/icon';
import useFocusTrap from './useFocusTrap';
import { displayContextShape, feedbackFormShape } from './feedbackPropTypes';
import { BACK_LINK_ARIA_LABEL, BACK_LINK_TEXT, FEEDBACK_CLOSE_LABEL, PICKER_EMPTY_MESSAGE, PICKER_SUBTITLE, PICKER_TITLE, getFeedbackCategory, getFeedbackConfirmationNote } from './feedbackCopy';
export const PICKER_SCREEN = 'picker';
export const FORM_SCREEN = 'form';
export const CONFIRMATION_SCREEN = 'confirmation';
const HEADING_ELEMENT_ID = 't-feedback-panel-heading';
const getScreenHeader = (screen, category, name) => {
  if (screen === FORM_SCREEN) {
    return {
      badgeGlyph: category.badgeGlyph,
      title: name || category.title,
      subtitle: category.subtitle
    };
  }
  if (screen === CONFIRMATION_SCREEN) {
    return {
      badgeGlyph: null,
      title: category.confirmationTitle,
      subtitle: ''
    };
  }
  return {
    badgeGlyph: null,
    title: PICKER_TITLE,
    subtitle: PICKER_SUBTITLE
  };
};
const FeedbackPanel = ({
  isOpen,
  onClose,
  onSubmit,
  onSelectCategory,
  onDiscardDraft,
  context,
  forms: _forms = [],
  screenshotsAllowed: _screenshotsAllowed = true,
  screenshotBlob: _screenshotBlob = null,
  isCapturingScreenshot: _isCapturingScreenshot = false,
  hasScreenshotError: _hasScreenshotError = false,
  onRemoveScreenshot,
  onRetakeScreenshot
}) => {
  const [screen, setScreen] = useState(PICKER_SCREEN);
  const [categoryId, setCategoryId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const panelRef = useRef(null);
  const headingRef = useRef(null);
  // Tracks prior open state so we only discard the shared formModal draft on a real close,
  // never while the panel is already closed (mount / prop churn must not wipe page FormModal).
  const wasOpenRef = useRef(false);
  const [screenshotAttachedOnSubmit, setScreenshotAttachedOnSubmit] = useState(false);
  const category = getFeedbackCategory(categoryId);
  const selectedForm = _forms.find(form => form.categoryId === categoryId);
  const resetToPicker = useCallback(({
    discardDraft
  }) => {
    setScreen(PICKER_SCREEN);
    setCategoryId(null);
    setHasSubmitError(false);
    setScreenshotAttachedOnSubmit(false);
    if (discardDraft) {
      onDiscardDraft();
    }
  }, [onDiscardDraft]);
  const returnToPicker = useCallback(() => {
    resetToPicker({
      discardDraft: true
    });
  }, [resetToPicker]);

  // Closing - via the X, the overlay or Escape - resets local UI and discards the feedback draft
  // so the next visit starts from the category picker. Idle closed state does nothing.
  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
      if (wasOpenRef.current) {
        resetToPicker({
          discardDraft: true
        });
      }
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, resetToPicker]);
  useFocusTrap(isOpen, panelRef, onClose);

  // Moving focus to the heading of each in-place screen keeps screen readers oriented as the
  // panel swaps content without a route change.
  useEffect(() => {
    if (isOpen && headingRef.current) {
      headingRef.current.focus();
    }
  }, [isOpen, screen]);
  const handleSelectCategory = async nextCategoryId => {
    // The draft has to exist before the form screen renders, so the editor never sees an
    // empty format.
    await onSelectCategory(nextCategoryId);
    setCategoryId(nextCategoryId);
    setScreen(FORM_SCREEN);
  };
  const handleSubmit = async values => {
    setIsSubmitting(true);
    setHasSubmitError(false);
    try {
      const result = await onSubmit({
        categoryId,
        values
      });
      setScreenshotAttachedOnSubmit(Boolean(result === null || result === void 0 ? void 0 : result.screenshotAttached));
      setScreen(CONFIRMATION_SCREEN);
    } catch (_unused) {
      setHasSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderScreen = () => {
    if (screen === FORM_SCREEN) {
      return /*#__PURE__*/React.createElement(FeedbackForm, {
        category: category,
        context: context,
        onBack: returnToPicker,
        onSubmit: handleSubmit,
        isSubmitting: isSubmitting,
        hasSubmitError: hasSubmitError,
        screenshotsAllowed: _screenshotsAllowed,
        screenshotBlob: _screenshotBlob,
        isCapturingScreenshot: _isCapturingScreenshot,
        hasScreenshotError: _hasScreenshotError,
        onRemoveScreenshot: onRemoveScreenshot,
        onRetakeScreenshot: onRetakeScreenshot
      });
    }
    if (screen === CONFIRMATION_SCREEN) {
      return /*#__PURE__*/React.createElement(FeedbackConfirmation, {
        note: getFeedbackConfirmationNote(categoryId, {
          screenshotAttached: screenshotAttachedOnSubmit
        }),
        onDone: onClose
      });
    }
    if (!_forms.length) {
      return /*#__PURE__*/React.createElement("p", {
        className: "t-feedback-empty",
        role: "status"
      }, PICKER_EMPTY_MESSAGE);
    }
    return /*#__PURE__*/React.createElement(FeedbackCategoryPicker, {
      forms: _forms,
      onSelect: handleSelectCategory
    });
  };
  const {
    badgeGlyph,
    title,
    subtitle
  } = getScreenHeader(screen, category, selectedForm === null || selectedForm === void 0 ? void 0 : selectedForm.name);
  const isBackVisible = screen !== PICKER_SCREEN;
  return /*#__PURE__*/React.createElement("div", {
    ref: panelRef,
    className: `t-feedback-panel${isOpen ? ' t-feedback-panel--open' : ''}`,
    role: "dialog",
    "aria-modal": "true",
    "aria-hidden": !isOpen,
    "aria-labelledby": HEADING_ELEMENT_ID
  }, /*#__PURE__*/React.createElement("div", {
    className: "t-feedback-panel__bar"
  }, isBackVisible ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "t-feedback-panel__back",
    "aria-label": BACK_LINK_ARIA_LABEL,
    onClick: returnToPicker
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "angle-left"
  })), ` ${BACK_LINK_TEXT}`) : /*#__PURE__*/React.createElement("span", {
    className: "t-feedback-panel__bar-spacer"
  }), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "t-feedback-panel__close",
    "aria-label": FEEDBACK_CLOSE_LABEL,
    onClick: onClose
  }, /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "times"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "t-feedback-panel__body"
  }, /*#__PURE__*/React.createElement("h3", {
    id: HEADING_ELEMENT_ID,
    ref: headingRef,
    tabIndex: -1,
    className: "t-feedback-screen__heading"
  }, badgeGlyph ? /*#__PURE__*/React.createElement("span", {
    className: `t-feedback-badge t-feedback-badge--${categoryId}`,
    "aria-hidden": "true"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: badgeGlyph
  })) : null, title), subtitle ? /*#__PURE__*/React.createElement("p", {
    className: "t-feedback-screen__subtitle"
  }, subtitle) : null, renderScreen()));
};
FeedbackPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSelectCategory: PropTypes.func.isRequired,
  onDiscardDraft: PropTypes.func.isRequired,
  context: displayContextShape.isRequired,
  forms: PropTypes.arrayOf(feedbackFormShape),
  screenshotsAllowed: PropTypes.bool,
  screenshotBlob: PropTypes.instanceOf(Blob),
  isCapturingScreenshot: PropTypes.bool,
  hasScreenshotError: PropTypes.bool,
  onRemoveScreenshot: PropTypes.func.isRequired,
  onRetakeScreenshot: PropTypes.func.isRequired
};
export default FeedbackPanel;